import { useMemo } from 'react';
import { format } from 'date-fns';
import {
  Appointment,
  FooterAction,
  GeneralRequest,
  LoadedUser,
} from '../../interfaces';
import useGetUnreadRequests from '../../hooks/requests/useGetUnreadRequests';
import VirtualizedTable from '../table/VirtualizedTable';
import { Sort } from '../../types/ui';
import { Columns, DataFromColumns } from '../../types/reports';
import {
  getEmployeeNameFromAppointmentOrGenRequest,
  getFullTattooLocation,
} from '../../utils/global/helper';
import { getServiceLabel } from '../../helpers/services';
import useUser from '../../hooks/global/useUser';
import type { Tab } from '../../routes/RequestsPage';
import { getAllRefImages } from '../../helpers/appointments';
import CustomerNameAge, { EXTRA_WIDTH } from './CustomerNameAge';

interface Props {
  requests: (Appointment | GeneralRequest)[];
  tab: Tab;
  isLoading: boolean;
  sort: Sort;
  count?: number;
  getActions: (request: Appointment | GeneralRequest) => FooterAction[];
  beforeAction: (request: Appointment | GeneralRequest) => void;
  onEndReached: () => void | Promise<void>;
  onOpenRequest: (request: Appointment | GeneralRequest) => void;
}

function RequestsTable({
  requests,
  tab,
  sort,
  isLoading,
  count,
  getActions,
  beforeAction,
  onEndReached,
  onOpenRequest,
}: Props) {
  const { data: unreadRequests } = useGetUnreadRequests();
  const { user: loadedUser } = useUser<LoadedUser>();

  const employeesToBusiness = loadedUser?.business?.employees;
  const hasNonTattooServices = !!loadedUser?.business?.services?.length;

  const data = useMemo(
    () =>
      requests.map(request => {
        const refImages = getAllRefImages(request);
        const employeeName = getEmployeeNameFromAppointmentOrGenRequest(
          request,
          employeesToBusiness,
        );
        const actions = getActions(request);
        // ItemViews decides whether a marker shows at all, so opening the
        // request clears it; the decline only decides which marker
        const isUnread = !!unreadRequests?.appointments.includes(
          (request as Appointment).appointmentId,
        );
        const isDeclined = !!(request as Appointment).offeredTimesDeclinedAt;
        return {
          image: {
            imageUrl: refImages?.[0]!,
            badge: isUnread && !isDeclined,
            needsAttention: isUnread && isDeclined,
          },
          employee: employeeName,
          service:
            'appointmentId' in request
              ? getServiceLabel(
                  (request as Appointment).serviceCategory,
                  loadedUser?.business?.services?.find(
                    s => s.serviceId === (request as Appointment).serviceId,
                  )?.serviceName,
                )
              : 'Tattoo',
          customer: {
            component: <CustomerNameAge appointment={request} />,
          },
          tattooLocation:
            getFullTattooLocation(request.tattooLocation, request.bodySide) ||
            'Not provided',
          createdAt: format(new Date(request.createdAt!), 'MMMM d, y'),
          actions:
            actions.length > 0
              ? {
                  actions: getActions(request).map(action => ({
                    ...action,
                    action: () => {
                      beforeAction(request);
                      setTimeout(() => {
                        action.action();
                      }, 0);
                    },
                  })),
                }
              : undefined,
          button: !actions.length
            ? {
                button: {
                  label: 'View Request',
                  onClick: () => onOpenRequest(request),
                  disabled: false,
                },
              }
            : undefined,
          appointmentId:
            'appointmentId' in request
              ? request.appointmentId
              : request.requestId,
          request,
        };
      }),
    [
      beforeAction,
      employeesToBusiness,
      getActions,
      loadedUser?.business?.services,
      onOpenRequest,
      requests,
      unreadRequests?.appointments,
    ],
  );

  const noActions = data.every(request => !request.actions);

  const columns = [
    {
      dataKey: 'image',
      label: 'Images',
      width: 'auto',
    },
    {
      dataKey: 'employee',
      label: 'Artist',
      width: 'auto',
    },
    {
      dataKey: 'service',
      label: 'Service',
      width: 'auto',
      hidden: !hasNonTattooServices,
    },
    {
      dataKey: 'customer',
      label: 'Client',
      width: 'auto',
      extraWidth: EXTRA_WIDTH,
    },
    {
      dataKey: 'tattooLocation',
      label: 'Tattoo Location',
      width: 'auto',
    },
    {
      dataKey: 'createdAt',
      label: 'Submitted On',
      width: 'auto',
    },
    {
      dataKey: 'actions',
      label: '',
      width: 150,
      hidden: noActions || !data.length,
    },
    {
      dataKey: 'button',
      label: '',
      hidden: !noActions || !data.length,
      width: 224,
    },
  ] as const satisfies Columns;

  const label =
    tab === 'REQUESTED'
      ? 'New Requests'
      : tab === 'ACCEPTED'
      ? 'Accepted Requests'
      : tab === 'REJECTED'
      ? 'Rejected Requests'
      : tab === 'CANCELED'
      ? 'Canceled by Customer'
      : tab === 'ARCHIVED'
      ? 'Archived Requests'
      : '';

  return (
    <VirtualizedTable
      type="regular"
      isLoading={isLoading}
      columns={columns}
      data={data as DataFromColumns<typeof columns>[]}
      emptyText={`No ${
        tab === 'REQUESTED' ? 'new' : tab.toLowerCase()
      } requests to show right now. If you believe this is an error, please reach out to our support team.`}
      headerContent={{
        title: label,
        subtitle: count ? `${count} requests to show` : undefined,
      }}
      height={480}
      onEndReached={onEndReached}
      sort={sort}
      onRowClick={row => onOpenRequest((row as (typeof data)[number]).request)}
    />
  );
}

export default RequestsTable;
